// CRM Command Center — the dashboard as a deal radar, not a card grid.
//
// Centre: an SVG radar scope built around one idea — distance from the core
// is WHEN you should act. Three priority bands (NOW / NEXT / WATCH) are
// filled with golden-angle spirals, so any number of deals spreads evenly
// with zero overlap. Blip colour = WHY it needs you (overdue follow-up,
// at-risk, queued, steady), blip size = deal value, red pulse = overdue.
// The outer arc ring is the real pipeline stages; the core shows open
// pipeline value, win rate and a derived health score. Clicking a blip
// locks it in a target readout.
//
// Right rail: mission control — an AI copilot (the backend's tool-using
// agent via /chat/stream), a computed priority queue, live stats, meetings
// and the activity pulse.
//
// Data: the existing /dashboard/overview payload + one leads-list fetch.
// All derivation lives in commandSignals.mjs (pure, self-checked).

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crosshair, Zap, Radio, LayoutGrid, ChevronRight, X, Video, ExternalLink,
  TrendingUp, TrendingDown, AlertCircle, Loader2, Calendar, Sparkles, RefreshCw, Bell,
} from "lucide-react";
import { useDashboard } from "../hooks/useActivities";
import { useLeads } from "../hooks/useLeads";
import { usePendingFollowUps } from "../hooks/useFollowUps";
import { useOrgCurrency } from "../hooks/useOrgCurrency";
import { useMyCrmRole } from "../hooks/useTeamMembers";
import { useSelectedProject } from "../contexts/SelectedProjectProvider";
import { useAuth } from "../../../contexts/AuthContext";
import { useAiStream } from "../../../pages/DashboardV2/hooks/useAiStream";
import { formatCurrency as fmtCurrency, getCurrencySymbol } from "../lib/currency";
import { websiteHref, mailtoHref } from "../lib/links";
import { useCrmBreakpoint } from "../hooks/useCrmBreakpoint";
import ActivityRow from "../molecules/ActivityRow";
import ProjectSelector from "../molecules/ProjectSelector";
import CompanySwitcher from "../molecules/CompanySwitcher";
import { Button } from "../atoms";
import { buildBlips, buildMissionQueue, healthScore, isOverdue, idleDaysOf, leadRules, copilotPrompt, dueAtOf, dueLabel } from "./commandSignals.mjs";

const MONO = 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, monospace';

const KEYFRAMES = `
@keyframes tgCcLiveDot { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
.tg-cc-live { animation: tgCcLiveDot 1.6s ease-in-out infinite; }
@keyframes tgCcBreath { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
.tg-cc-breath { animation: tgCcBreath 3s ease-in-out infinite; }
@keyframes tgCcSpin { to { transform: rotate(360deg); } }
`;

// Blip colour = why the deal needs you. Matches the queue severity tones.
const SEV_TONE = {
  critical: "var(--crm-danger, #E5484D)",
  high: "var(--crm-warning, #A29B4E)",
  medium: "var(--crm-info, #4273D1)",
  growth: "var(--crm-success, #50AB5B)",
};
const STEADY_TONE = "var(--crm-neutral, #757677)";
const blipTone = (sev) => SEV_TONE[sev] || STEADY_TONE;

const fmtCompact = (n, currency) => {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency: currency || "USD", notation: "compact", maximumFractionDigits: 1 }).format(n || 0);
  } catch {
    return `${getCurrencySymbol(currency)}${Math.round(n || 0).toLocaleString()}`;
  }
};

const meetingTime = (start) => {
  if (!start) return "";
  const s = new Date(start);
  const today = new Date().toDateString() === s.toDateString();
  const day = today ? "Today" : s.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${day} · ${s.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
};

/* ── Radar geometry (viewBox 760×760) ── */
const C = 380;
// Priority annuli: NOW hugs the core, WATCH patrols the rim.
const BAND_GEO = { now: [128, 170], next: [182, 232], watch: [244, 292] };
const R_SWEEP = 292;
const R_STAGE = 316;
const R_CORE = 92, R_WIN = 106;
const rad = (d) => (d * Math.PI) / 180;
const pt = (r, d) => [C + r * Math.cos(rad(d)), C + r * Math.sin(rad(d))];
const arcPath = (r, a0, a1) => {
  const [x0, y0] = pt(r, a0);
  const [x1, y1] = pt(r, a1);
  return `M ${x0} ${y0} A ${r} ${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${x1} ${y1}`;
};
// Labels float over blips and rings, so they carry a bg-coloured halo stroke.
const HALO = { paintOrder: "stroke", stroke: "var(--color-bg-base)", strokeWidth: 5, strokeLinejoin: "round" };
// Band labels live in the empty gaps between annuli, never on the blips.
const BAND_LABEL_R = { now: 176, next: 238, watch: 303 };

function Radar({ blipData, stages, openValue, winRate, health, activeCount, currency, target, onTarget }) {
  const healthTone = health == null ? "var(--color-text-secondary)"
    : health >= 65 ? "var(--crm-success, #50AB5B)"
      : health >= 40 ? "var(--crm-warning, #A29B4E)"
        : "var(--crm-danger, #E5484D)";

  // Stage ring: arc length ∝ open leads in that stage, 6° gaps.
  const totalStage = stages.reduce((s, x) => s + x.value, 0);
  const gaps = stages.length * 6;
  let cursor = -90;
  const stageArcs = stages.map((s) => {
    const span = totalStage > 0 ? (s.value / totalStage) * (360 - gaps) : 0;
    const arc = { ...s, a0: cursor, a1: cursor + span, mid: cursor + span / 2 };
    cursor += span + 6;
    return arc;
  });
  const maxStage = stages.reduce((m, s) => Math.max(m, s.value), 0);

  const winC = 2 * Math.PI * R_WIN;

  return (
    <svg viewBox="0 0 760 760" style={{ width: "100%", height: "100%", display: "block" }} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="tgCcSweep" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" style={{ stopColor: "var(--crm-primary)", stopOpacity: 0.2 }} />
          <stop offset="100%" style={{ stopColor: "var(--crm-primary)", stopOpacity: 0 }} />
        </linearGradient>
        <radialGradient id="tgCcAura">
          <stop offset="0%" style={{ stopColor: healthTone, stopOpacity: 0.12 }} />
          <stop offset="60%" style={{ stopColor: healthTone, stopOpacity: 0.03 }} />
          <stop offset="100%" style={{ stopColor: healthTone, stopOpacity: 0 }} />
        </radialGradient>
      </defs>

      <circle cx={C} cy={C} r={280} fill="url(#tgCcAura)" />

      {/* priority bands: soft annulus shading + dashed edges + axis labels */}
      {blipData.bands.map((band, i) => {
        const [r0, r1] = BAND_GEO[band.key];
        const mid = (r0 + r1) / 2;
        // Each label is a solid pill chip in its own gap ring on the top-left
        // diagonal — opaque backdrop, so nothing beneath can garble it.
        const [lx, ly] = pt(BAND_LABEL_R[band.key], -135);
        const label = `${band.label} · ${band.total}${band.key === "watch" && band.overflow > 0 ? ` +${band.overflow}` : ""}`;
        const pillW = label.length * 8.2 + 20; // mono 11px + tracking, eyeballed advance
        return (
          <g key={band.key}>
            <circle cx={C} cy={C} r={mid} fill="none" strokeWidth={r1 - r0} style={{ stroke: "var(--crm-primary)" }} opacity={[0.05, 0.035, 0.02][i]} />
            <circle cx={C} cy={C} r={r1} fill="none" style={{ stroke: "var(--color-border-subtle)" }} strokeDasharray="2 6" opacity={0.8} />
            <rect x={lx - pillW / 2} y={ly - 11} width={pillW} height={22} rx={11}
              style={{ fill: "var(--color-bg-surface)", stroke: "var(--color-border-subtle)" }} strokeWidth={1} />
            <circle cx={lx - pillW / 2 + 12} cy={ly} r={3} style={{ fill: "var(--crm-primary)" }} opacity={0.9} />
            <text x={lx + 6} y={ly} textAnchor="middle" dominantBaseline="central"
              style={{ fill: "var(--color-text-primary)", fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.14em" }}>
              {label}
            </text>
          </g>
        );
      })}

      {/* rotating sweep — annular wedge, starts outside the core so it never
          paints a triangle over the centre readout */}
      <g opacity={0.5}>
        <path
          d={`M ${pt(R_WIN + 10, -13).join(" ")} L ${pt(R_SWEEP, -13).join(" ")} A ${R_SWEEP} ${R_SWEEP} 0 0 1 ${pt(R_SWEEP, 13).join(" ")} L ${pt(R_WIN + 10, 13).join(" ")} A ${R_WIN + 10} ${R_WIN + 10} 0 0 0 ${pt(R_WIN + 10, -13).join(" ")} Z`}
          fill="url(#tgCcSweep)"
        >
          <animateTransform attributeName="transform" type="rotate" from={`0 ${C} ${C}`} to={`360 ${C} ${C}`} dur="12s" repeatCount="indefinite" />
        </path>
      </g>

      {/* pipeline stage ring */}
      {stageArcs.map((s) => (
        <g key={s.name}>
          <path d={arcPath(R_STAGE, s.a0, s.a1)} fill="none" strokeWidth={6} strokeLinecap="round"
            style={{ stroke: "var(--crm-primary)" }} opacity={0.2 + 0.35 * (maxStage ? s.value / maxStage : 0)}>
            <title>{`${s.name} — ${s.value} open lead${s.value === 1 ? "" : "s"}`}</title>
          </path>
          <circle cx={pt(R_STAGE, s.mid)[0]} cy={pt(R_STAGE, s.mid)[1]} r={2.5} style={{ fill: "var(--crm-primary)" }} opacity={0.8} />
          <text x={pt(R_STAGE + 22, s.mid)[0]} y={pt(R_STAGE + 22, s.mid)[1]} textAnchor="middle" dominantBaseline="middle"
            style={{ fill: "var(--color-text-secondary)", fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.06em", ...HALO }}>
            {s.name.length > 14 ? `${s.name.slice(0, 13)}…` : s.name} · {s.value}
          </text>
        </g>
      ))}

      {/* deal blips — golden-angle spirals per band, urgency-coloured */}
      {blipData.bands.map((band) => {
        const [r0, r1] = BAND_GEO[band.key];
        // Dense bands get slimmer blips so value-sized neighbours can't touch.
        const sMax = band.blips.length > 60 ? 3 : 5;
        return band.blips.map((b) => {
          const r = r0 + b.r01 * (r1 - r0);
          const [x, y] = pt(r, b.angleDeg);
          const s = 3 + b.size01 * sMax;
          const tone = blipTone(b.severity);
          const locked = target && String(target._id) === b.id;
          return (
            <g key={b.id} onClick={() => onTarget(b.lead)} style={{ cursor: "pointer" }}>
              <title>{`${b.lead.title}${b.lead.value ? ` · ${fmtCurrency(b.lead.value, currency)}` : ""} · ${(b.lead.status || "").toUpperCase()} · ${b.reason}`}</title>
              <circle cx={x} cy={y} r={14} fill="transparent" />
              {b.overdue && (
                <circle cx={x} cy={y} r={s} fill="none" strokeWidth={1.5} style={{ stroke: "var(--crm-danger, #E5484D)" }}>
                  <animate attributeName="r" values={`${s};${s + 11}`} dur="1.7s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.8;0" dur="1.7s" repeatCount="indefinite" />
                </circle>
              )}
              {locked && <circle cx={x} cy={y} r={s + 6} fill="none" strokeWidth={1.5} strokeDasharray="3 3" style={{ stroke: "var(--color-text-primary)" }} />}
              <circle cx={x} cy={y} r={s} style={{ fill: tone }} opacity={b.severity ? 0.95 : 0.5} />
              {b.severity === "critical" && <circle cx={x} cy={y} r={s + 2.5} fill="none" strokeWidth={1} style={{ stroke: tone }} opacity={0.5} />}
            </g>
          );
        });
      })}

      {/* core */}
      <circle cx={C} cy={C} r={R_WIN} fill="none" strokeWidth={5} style={{ stroke: "var(--color-border-subtle)" }} />
      <circle cx={C} cy={C} r={R_WIN} fill="none" strokeWidth={5} strokeLinecap="round"
        style={{ stroke: healthTone }}
        strokeDasharray={`${Math.max(0.02, winRate) * winC} ${winC}`} transform={`rotate(-90 ${C} ${C})`} />
      <circle cx={C} cy={C} r={R_CORE + 8} fill="none" strokeWidth={1} strokeDasharray="2 7" style={{ stroke: healthTone }} opacity={0.7}>
        <animateTransform attributeName="transform" type="rotate" from={`0 ${C} ${C}`} to={`360 ${C} ${C}`} dur="50s" repeatCount="indefinite" />
      </circle>
      <circle cx={C} cy={C} r={R_CORE} style={{ fill: "var(--color-bg-surface)", stroke: "var(--color-border-subtle)" }} strokeWidth={1} />
      <text x={C} y={C - 38} textAnchor="middle" style={{ fill: "var(--color-text-secondary)", fontFamily: MONO, fontSize: 10, letterSpacing: "0.3em" }}>OPEN PIPELINE</text>
      <text x={C} y={C + 2} textAnchor="middle" style={{ fill: "var(--color-text-primary)", fontFamily: MONO, fontSize: 36, fontWeight: 600 }}>
        {activeCount > 0 || openValue > 0 ? fmtCompact(openValue, currency) : "—"}
      </text>
      <text x={C} y={C + 28} textAnchor="middle" style={{ fill: "var(--color-text-secondary)", fontFamily: MONO, fontSize: 11.5 }}>
        {activeCount} ACTIVE · WIN {Math.round((winRate || 0) * 100)}%
      </text>
      <text className="tg-cc-breath" x={C} y={C + 52} textAnchor="middle" style={{ fill: healthTone, fontFamily: MONO, fontSize: 11, fontWeight: 600, letterSpacing: "0.14em" }}>
        {health == null ? "STANDBY" : `HEALTH ${health}`}
      </text>
    </svg>
  );
}

/* ── AI copilot — the backend's tool-using agent reads the pipeline ── */
const COPILOT_CACHE = "tg-crm-copilot-v1";
const COPILOT_TTL = 15 * 60 * 1000;
const TAG_TONE = { risk: "var(--crm-danger, #E5484D)", push: "var(--crm-warning, #A29B4E)", win: "var(--crm-success, #50AB5B)" };

function CopilotLine({ line, idx }) {
  let tone = null;
  const text = line.replace(/^\s*(?:[-*]\s*)?(?:\d+[.)]\s*)?\[(risk|push|win)\]\s*/i, (_, tag) => {
    tone = TAG_TONE[tag.toLowerCase()];
    return "";
  });
  const color = tone || "var(--crm-primary)";
  const parts = text.split(/\*\*/);
  return (
    <div key={idx} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, lineHeight: "18px", color: "var(--color-text-secondary)", marginTop: 7 }}>
      <span style={{ width: 7, height: 7, borderRadius: 99, background: color, flexShrink: 0, marginTop: 5 }} />
      <span>{parts.map((p, i) => (i % 2 === 1 ? <strong key={i} style={{ color, fontWeight: 600 }}>{p}</strong> : p))}</span>
    </div>
  );
}

function CrmCopilot({ ready, prompt, scopeKey }) {
  const ai = useAiStream("/api/v1/chat/stream");
  const [cachedText, setCachedText] = React.useState(null);
  const startedRef = React.useRef(false);
  const cacheKey = `${COPILOT_CACHE}:${scopeKey}`;

  const think = React.useCallback(() => {
    setCachedText(null);
    ai.run({ message: prompt, conversationHistory: [] });
  }, [ai, prompt]);

  React.useEffect(() => {
    if (!ready || startedRef.current) return;
    startedRef.current = true;
    try {
      const c = JSON.parse(sessionStorage.getItem(cacheKey) || "null");
      if (c?.text && Date.now() - c.ts < COPILOT_TTL) { setCachedText(c.text); return; }
    } catch (_) {}
    think();
  }, [ready, think, cacheKey]);

  // A reply without our tags means the agent refused / rambled — don't cache
  // it and don't render it, fall back to the deterministic queue instead.
  const usable = (s) => /\[(risk|push|win)\]/i.test(s || "");

  React.useEffect(() => {
    if (!ai.streaming && ai.text && usable(ai.text)) {
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ text: ai.text, ts: Date.now() })); } catch (_) {}
    }
  }, [ai.streaming, ai.text, cacheKey]);

  const raw = cachedText ?? ai.text;
  const refused = !ai.streaming && !!raw && !usable(raw);
  const text = refused ? "" : raw;
  // While streaming, only surface tagged lines so a refusal never flashes by.
  const lines = text.split("\n").filter((l) => l.trim() && usable(l));

  return (
    <div style={{ padding: 16, borderBottom: "1px solid var(--color-border-subtle)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <RailLabel icon={<Sparkles size={11} />} color="var(--crm-primary)">Copilot · agent</RailLabel>
        <button
          onClick={think}
          disabled={ai.streaming || !ready}
          title="Ask the agent to re-read the pipeline"
          aria-label="Refresh copilot"
          style={{ width: 24, height: 24, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 6, border: "none", background: "transparent", color: "var(--color-text-secondary)", cursor: ai.streaming ? "default" : "pointer" }}
        >
          <RefreshCw size={12} style={ai.streaming ? { animation: "tgCcSpin 1s linear infinite" } : undefined} />
        </button>
      </div>
      {ai.limitReached && (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--color-text-secondary)" }}>
          AI limit reached — the deterministic queue below still has you covered.
        </div>
      )}
      {!ai.limitReached && lines.length === 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--color-text-secondary)" }}>
          {ai.streaming ? "The agent is reading your pipeline…"
            : refused ? "The agent couldn't read this one — the priority queue below has you covered."
              : ai.error ? "Agent unavailable — using the queue below." : "Waiting for pipeline data…"}
        </div>
      )}
      {lines.map((l, i) => <CopilotLine key={i} line={l} idx={i} />)}
      {ai.streaming && text && (
        <span style={{ display: "inline-block", width: 6, height: 12, marginTop: 4, background: "var(--crm-primary)", animation: "tgCcLiveDot 1s ease-in-out infinite" }} />
      )}
    </div>
  );
}

/* ── Right rail bits ── */
function RailLabel({ icon, color, children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, letterSpacing: "0.09em", textTransform: "uppercase", color: "var(--color-text-secondary)", fontWeight: 600 }}>
      <span style={{ color, display: "inline-flex" }}>{icon}</span>{children}
    </span>
  );
}

function Stat({ value, label, color, first, bottom, change }) {
  const Trend = change?.direction === "up" ? TrendingUp : TrendingDown;
  return (
    <div style={{ padding: "12px 16px", borderLeft: first ? "none" : "1px solid var(--color-border-subtle)", borderTop: bottom ? "1px solid var(--color-border-subtle)" : "none" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontSize: 19, fontWeight: 600, color, fontVariantNumeric: "tabular-nums", fontFamily: MONO }}>{value}</span>
        {change && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 10, fontWeight: 600, color: change.direction === "up" ? "var(--crm-success)" : "var(--crm-danger)" }}>
            <Trend size={10} />{change.value}%
          </span>
        )}
      </div>
      <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-text-secondary)", marginTop: 1 }}>{label}</div>
    </div>
  );
}

/* ── Target panel — docked next to the radar when a blip is locked ── */
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null);

function Fact({ k, v, href }) {
  if (!v) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderBottom: "1px dashed var(--color-border-subtle)" }}>
      <span style={{ fontSize: 10, letterSpacing: "0.08em", color: "var(--color-text-secondary)", textTransform: "uppercase", flexShrink: 0, paddingTop: 2 }}>{k}</span>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 12, fontFamily: MONO, color: "var(--crm-primary)", textAlign: "right", overflowWrap: "anywhere", textDecoration: "none" }}>{v}</a>
      ) : (
        <span style={{ fontSize: 12, fontFamily: MONO, color: "var(--color-text-primary)", textAlign: "right", overflowWrap: "anywhere" }}>{v}</span>
      )}
    </div>
  );
}

function TargetPanel({ lead, now, currency, isMobile, onClose, onOpenPipeline }) {
  const idle = idleDaysOf(lead, now);
  const rules = leadRules(lead, now, { fmtValue: (v) => fmtCurrency(v, currency) }).slice(0, 3);
  const fu = lead.followUp;
  const fuText = fu?.status === "pending"
    ? `${fmtDate(fu.snoozedUntil || fu.scheduledAt)}${isOverdue(lead, now) ? " · OVERDUE" : ""}`
    : "None scheduled";
  // The tab URL pattern Preview.jsx owns: /<dashboard base>/crm/<view>.
  const crmBase = window.location.pathname.replace(/\/crm(\/[^/]*)?\/?$/, "/crm");
  const leadUrl = `${crmBase}/leads?leadId=${lead._id}`;

  return (
    <motion.div
      initial={{ opacity: 0, x: isMobile ? 0 : 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: isMobile ? 0 : 24 }}
      transition={{ duration: 0.18 }}
      style={{
        width: isMobile ? "auto" : 296, flexShrink: 0, alignSelf: isMobile ? "stretch" : "center",
        maxHeight: isMobile ? "none" : "calc(100% - 84px)", margin: isMobile ? "0 12px 12px" : "64px 16px 12px 0",
        display: "flex", flexDirection: "column",
        background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 12,
        boxShadow: "0 12px 32px rgba(0,0,0,0.14)", overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderBottom: "1px solid var(--color-border-subtle)" }}>
        <Crosshair size={14} style={{ color: "var(--crm-primary)", flexShrink: 0 }} />
        <span style={{ fontSize: 10.5, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.18em", color: "var(--color-text-secondary)", flex: 1 }}>TARGET LOCK</span>
        <button onClick={onClose} aria-label="Clear target"
          style={{ border: "none", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", display: "flex", padding: 2 }}>
          <X size={14} />
        </button>
      </div>

      <div style={{ padding: "12px 14px", overflowY: "auto", flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600, lineHeight: "20px" }}>{lead.title}</div>
        <div style={{ fontSize: 11, fontFamily: MONO, color: "var(--color-text-secondary)", marginTop: 3 }}>
          {(lead.status || "").toUpperCase()}{lead.brandName ? ` · ${lead.brandName}` : ""}
        </div>

        {/* why it's flagged — same rules engine as the queue and the blips */}
        <div style={{ margin: "12px 0 4px", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--color-text-secondary)", fontWeight: 600 }}>Signals</div>
        {rules.length === 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--color-text-secondary)" }}>
            <span style={{ width: 6, height: 6, borderRadius: 99, background: STEADY_TONE, opacity: 0.6 }} />
            Steady — nothing flagged on this deal.
          </div>
        ) : rules.map((r) => (
          <div key={r.kind + r.weight} style={{ display: "flex", alignItems: "flex-start", gap: 7, marginTop: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: 99, background: SEV_TONE[r.severity], flexShrink: 0, marginTop: 5 }} />
            <span style={{ fontSize: 12, color: "var(--color-text-primary)", lineHeight: "17px" }}>{r.evidence}</span>
          </div>
        ))}

        <div style={{ margin: "14px 0 2px", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--color-text-secondary)", fontWeight: 600 }}>Summary</div>
        <Fact k="Value" v={lead.value ? fmtCurrency(lead.value, lead.currency || currency) : "—"} />
        <Fact k="Idle" v={`${idle} day${idle === 1 ? "" : "s"}`} />
        <Fact k="Follow-up" v={fuText} />
        <Fact k="Exp. close" v={fmtDate(lead.expectedCloseDate)} />
        <Fact k="Source" v={lead.source ? `${lead.source}${lead.sourceChannel ? ` / ${lead.sourceChannel}` : ""}` : null} />
        <Fact k="Owner" v={lead.ownerEmail} />
        <Fact k="Email" v={lead.email} href={mailtoHref(lead.email)} />
        <Fact k="Phone" v={lead.phone} href={lead.phone ? `tel:${lead.phone}` : null} />
        <Fact k="Website" v={lead.website} href={websiteHref(lead.website)} />
        <Fact k="Created" v={fmtDate(lead.createdAt)} />
      </div>

      <div style={{ padding: 12, borderTop: "1px solid var(--color-border-subtle)", display: "flex", flexDirection: "column", gap: 8 }}>
        {/* real anchor — new-tab open works even where window.open is blocked */}
        <a href={leadUrl} target="_blank" rel="noopener noreferrer"
          style={{ height: 34, borderRadius: 8, background: "var(--crm-primary)", color: "var(--crm-primary-text, #fff)", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, textDecoration: "none" }}>
          Open lead <ExternalLink size={13} />
        </a>
        <Button variant="secondary" size="sm" onClick={onOpenPipeline} style={{ width: "100%" }}>View in pipeline</Button>
      </div>
    </motion.div>
  );
}

export default function CommandCenter({ onNavigate, onNavigateToPipeline }) {
  const { data, isLoading, isError, error } = useDashboard();
  // One page of freshest leads powers blips + the mission queue. Backend caps
  // at 200. ponytail: >200 active leads = the radar plots the freshest 200
  // and says so on-screen; add a priority endpoint if that stops being enough.
  const leadsQ = useLeads({ limit: 200, sort: "updatedAt", order: "desc" });
  // Due-sorted and not bounded by freshness, so a follow-up on a lead that has
  // sat untouched for months still reaches the queue and the rail.
  const pendingQ = usePendingFollowUps({ scope: "mine", limit: 50 });
  const { user } = useAuth();
  const { isAdmin } = useMyCrmRole();
  const { project, projectId } = useSelectedProject();
  const { currency } = useOrgCurrency();
  const { isMobile } = useCrmBreakpoint();
  const [target, setTarget] = React.useState(null);

  const now = Date.now();
  const leads = React.useMemo(() => leadsQ.data?.items || [], [leadsQ.data]);
  const meetings = data?.upcomingMeetings || [];
  const counts = data?.counts || { totalLeads: 0, openLeads: 0, wonLeads: 0, lostLeads: 0 };
  const value = data?.value || { openValue: 0, wonValue: 0 };
  const changes = data?.changes || {};
  const winRate = data?.winRate || 0;
  const stages = data?.pipelineStages || [];
  const staleLeads = data?.staleLeads || { total3: 0, total7: 0, total14: 0 };
  const recentActivities = data?.recentActivities || [];

  // The radar plots what /leads returned, so its "plotting N of M" stays true.
  // Advice, though, must not miss a follow-up just because the lead is stale:
  // union the pending feed in for the queue and the overdue count. Projected
  // rows carry status + followUp, which is all the follow-up rules read.
  const pendingLeads = React.useMemo(() => pendingQ.data?.items || [], [pendingQ.data]);
  const advisoryLeads = React.useMemo(() => {
    const seen = new Set(leads.map((l) => String(l._id)));
    return leads.concat(pendingLeads.filter((l) => !seen.has(String(l._id))));
  }, [leads, pendingLeads]);

  // Soonest first — overdue rows lead, since dueAtOf is already in the past.
  const nextFollowUps = React.useMemo(() => (
    pendingLeads
      .map((lead) => ({ lead, at: dueAtOf(lead) }))
      .filter((x) => x.at > 0)
      .sort((a, b) => a.at - b.at)
      .slice(0, 5)
  ), [pendingLeads]);

  const blipData = React.useMemo(() => buildBlips(leads, now), [leads, now]);
  const queue = React.useMemo(
    () => buildMissionQueue({ leads: advisoryLeads, meetings, leadSources: data?.leadSources || [], winRate, now, isAdmin, fmtValue: (v) => fmtCompact(v, currency) }),
    [advisoryLeads, meetings, data?.leadSources, winRate, now, isAdmin, currency],
  );
  const health = React.useMemo(() => healthScore({ leads, winRate, now }), [leads, winRate, now]);

  const overdueCount = React.useMemo(() => advisoryLeads.filter((l) => isOverdue(l, now)).length, [advisoryLeads, now]);
  const statusCounts = React.useMemo(() => {
    const c = { hot: 0, warm: 0, cold: 0, open: 0 };
    for (const l of leads) if (l.status in c) c[l.status] += 1;
    return c;
  }, [leads]);
  const meetings24h = meetings.filter((m) => {
    const at = m.scheduledStart ? new Date(m.scheduledStart).getTime() : 0;
    return at >= now && at - now <= 86_400_000;
  }).length;
  const urgentCount = queue.filter((q) => q.severity === "critical" || q.severity === "high").length;

  const copilotReady = !isLoading && !leadsQ.isLoading && !isError && !!data;
  const copilotMessage = React.useMemo(
    () => copilotPrompt({ counts, value, winRate, staleLeads, overdueCount, meetings24h, queue, currencyLabel: getCurrencySymbol(currency) || "" }),
    [counts, value, winRate, staleLeads, overdueCount, meetings24h, queue, currency],
  );

  const runAction = (item) => {
    if (!item) return;
    if (item.kind === "meeting") { if (item.meetUri) window.open(item.meetUri, "_blank", "noopener"); return; }
    if (item.to === "pipeline" && item.idleFilter) { onNavigateToPipeline?.(item.idleFilter); return; }
    if (item.to) onNavigate?.(item.to);
  };

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  })();
  const firstName = user?.firstName || (user?.name || "").split(" ")[0] || "";

  const next = queue[0];
  const busy = isLoading || leadsQ.isLoading;

  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", height: "100%", minHeight: 0, overflow: "hidden", background: "var(--color-bg-base)", color: "var(--color-text-primary)" }}>
      <style>{KEYFRAMES}</style>

      {/* ═══ Centre stage ═══ */}
      <div style={{ position: "relative", flex: 1, minWidth: 0, minHeight: isMobile ? 460 : 0, display: "flex", flexDirection: "column" }}>
        {/* header overlay */}
        <div style={{ position: "absolute", top: 14, left: 20, right: 20, zIndex: 5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", pointerEvents: "none" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 500 }}>{greeting}{firstName ? `, ${firstName}` : ""}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, fontSize: 12, color: "var(--color-text-secondary)" }}>
              <span className="tg-cc-live" style={{ width: 6, height: 6, borderRadius: 99, background: "var(--crm-success)" }} />
              {project?.name || "All projects"} · {counts.openLeads} active · {urgentCount > 0 ? `${urgentCount} need action` : "runway clear"}
            </div>
          </div>
          <div className="tg-filterbar" style={{ pointerEvents: "auto", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <CompanySwitcher />
            <ProjectSelector />
            <Button variant="secondary" size="sm" leftIcon={<LayoutGrid size={13} />} onClick={() => onNavigate?.("dashboard")}>Classic</Button>
          </div>
        </div>

        {/* urgency legend + status counts, bottom-left */}
        {!busy && !isError && (
          <div style={{ position: "absolute", left: 20, bottom: 16, zIndex: 4, pointerEvents: "none" }}>
            {[["critical", "Overdue"], ["high", "At risk"], ["medium", "Queued"], [null, "Steady"]].map(([sev, label]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                <span style={{ width: 7, height: 7, borderRadius: 99, background: blipTone(sev), opacity: sev ? 1 : 0.5 }} />
                <span style={{ fontSize: 10.5, fontFamily: MONO, letterSpacing: "0.06em", color: "var(--color-text-secondary)" }}>{label.toUpperCase()}</span>
              </div>
            ))}
            <div style={{ marginTop: 8, fontSize: 10.5, fontFamily: MONO, letterSpacing: "0.05em", color: "var(--color-text-secondary)" }}>
              HOT {statusCounts.hot} · WARM {statusCounts.warm} · COLD {statusCounts.cold} · NEW {statusCounts.open}
            </div>
          </div>
        )}

        {/* radar + docked target panel */}
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: isMobile ? "column" : "row" }}>
          <div style={{ flex: 1, minWidth: 0, minHeight: isMobile ? 380 : 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "56px 8px 4px" }}>
            {busy ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--color-text-secondary)", fontFamily: MONO, fontSize: 13 }}>
                <Loader2 size={16} className="animate-spin" /> CALIBRATING RADAR…
              </div>
            ) : isError ? (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, color: "var(--crm-danger)", maxWidth: 420, fontSize: 13 }}>
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                {error?.response?.data?.error || error?.message || "Couldn't load the command center"}
              </div>
            ) : (
              <Radar
                blipData={blipData}
                stages={stages}
                openValue={value.openValue}
                winRate={winRate}
                health={health}
                activeCount={counts.openLeads}
                currency={currency}
                target={target}
                onTarget={(l) => setTarget((t) => (t && String(t._id) === String(l._id) ? null : l))}
              />
            )}
          </div>
          <AnimatePresence>
            {target && (
              <TargetPanel
                key={String(target._id)}
                lead={target}
                now={now}
                currency={currency}
                isMobile={isMobile}
                onClose={() => setTarget(null)}
                onOpenPipeline={() => onNavigate?.("pipeline")}
              />
            )}
          </AnimatePresence>
        </div>

        {/* scope line + alert chips */}
        {!busy && !isError && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7, padding: "0 16px 14px", zIndex: 4 }}>
            <div style={{ fontSize: 10, fontFamily: MONO, letterSpacing: "0.18em", color: "var(--color-text-secondary)" }}>
              PLOTTING {blipData.shown} OF {Math.max(counts.openLeads, blipData.activeTotal)} ACTIVE · PRIORITY ORDER · CORE = ACT NOW
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              {overdueCount > 0 && (
                <Chip tone="var(--crm-danger, #E5484D)" onClick={() => onNavigate?.("followups")}>{`FU OVERDUE · ${overdueCount}`}</Chip>
              )}
              {(staleLeads.total7 + staleLeads.total14) > 0 && (
                <Chip tone="var(--crm-warning, #A29B4E)" onClick={() => onNavigateToPipeline?.("7")}>{`IDLE 7D+ · ${staleLeads.total7 + staleLeads.total14}`}</Chip>
              )}
              {meetings24h > 0 && <Chip tone="var(--crm-info, #4273D1)">{`MTG 24H · ${meetings24h}`}</Chip>}
              {overdueCount === 0 && staleLeads.total7 + staleLeads.total14 === 0 && meetings24h === 0 && (
                <Chip tone="var(--crm-success, #50AB5B)">ALL CLEAR</Chip>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ═══ Right rail — mission control ═══ */}
      <aside style={{
        width: isMobile ? "100%" : 336, flexShrink: 0,
        borderLeft: isMobile ? "none" : "1px solid var(--color-border-subtle)",
        borderTop: isMobile ? "1px solid var(--color-border-subtle)" : "none",
        background: "var(--color-bg-surface)", display: "flex", flexDirection: "column", minHeight: 0, overflowY: "auto",
      }}>
        <div style={{ padding: "16px 16px 12px" }}>
          <div style={{ fontSize: 17, fontWeight: 600 }}>Mission control</div>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4, lineHeight: "17px" }}>
            {counts.openLeads > 0
              ? <>{counts.openLeads} active deal{counts.openLeads === 1 ? "" : "s"} worth {fmtCompact(value.openValue, currency)}. {urgentCount > 0 ? `${urgentCount} need${urgentCount === 1 ? "s" : ""} action now.` : "Nothing is on fire."}</>
              : "No active deals in this scope yet — the radar lights up as leads come in."}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: "1px solid var(--color-border-subtle)", borderBottom: "1px solid var(--color-border-subtle)" }}>
          <Stat value={fmtCompact(value.openValue, currency)} label="pipeline" color="var(--crm-primary)" change={changes.pipelineValue} first />
          <Stat value={`${Math.round(winRate * 100)}%`} label="win rate" color="var(--crm-success)" />
          <Stat value={counts.wonLeads} label="won" color="var(--crm-success)" change={changes.wonDeals} first bottom />
          <Stat value={overdueCount} label="fu overdue" color={overdueCount > 0 ? "var(--crm-danger)" : "var(--color-text-secondary)"} bottom />
        </div>

        {/* AI copilot — remounts per scope so advice follows the selector */}
        <CrmCopilot key={projectId || "all"} ready={copilotReady} prompt={copilotMessage} scopeKey={projectId || "all"} />

        {/* Do this next */}
        {next && (
          <div style={{ padding: 16, borderBottom: "1px solid var(--color-border-subtle)" }}>
            <RailLabel icon={<Zap size={11} />} color="var(--crm-primary)">Do this next</RailLabel>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 8, lineHeight: "19px" }}>{next.title}</div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 3, lineHeight: "17px" }}>{next.evidence}</div>
            {(next.to || next.meetUri) && (
              <button
                onClick={() => runAction(next)}
                style={{ marginTop: 10, width: "100%", height: 32, borderRadius: 7, border: "none", background: "var(--crm-primary)", color: "var(--crm-primary-text, #fff)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                {next.kind === "meeting" ? "Join / open meeting" : next.to === "followups" ? "Open follow-ups" : next.to === "leads" ? "Open leads" : next.to === "review" ? "Open review" : "Open pipeline"}
              </button>
            )}
          </div>
        )}

        {/* Next follow-ups — the commitments you already made, soonest first.
            The queue above ranks by urgency; this answers "when is my next
            one" even when nothing is on fire yet. */}
        <div style={{ padding: "10px 0", borderBottom: "1px solid var(--color-border-subtle)" }}>
          <div style={{ padding: "0 16px" }}>
            <RailLabel icon={<Bell size={11} />} color="var(--crm-warning)">
              Next follow-ups{pendingQ.data?.total ? ` · ${pendingQ.data.total}` : ""}
            </RailLabel>
          </div>
          {nextFollowUps.map(({ lead, at }) => {
            const late = at < now;
            return (
              <button
                key={String(lead._id)}
                onClick={() => onNavigate?.("followups")}
                style={{ display: "flex", alignItems: "flex-start", gap: 8, width: "100%", padding: "7px 16px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left", transition: "background 140ms" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--color-bg-muted)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ width: 6, height: 6, borderRadius: 99, background: late ? "var(--crm-danger)" : "var(--crm-warning)", flexShrink: 0, marginTop: 5 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 12.5, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.title}</span>
                  <span style={{ display: "block", fontSize: 11, color: late ? "var(--crm-danger)" : "var(--color-text-secondary)" }}>
                    {dueLabel(at, now)}
                    {lead.value ? ` · ${fmtCompact(lead.value, lead.currency || currency)}` : ""}
                  </span>
                </span>
                <ChevronRight size={13} style={{ color: "var(--color-text-secondary)", flexShrink: 0, marginTop: 3 }} />
              </button>
            );
          })}
          {nextFollowUps.length === 0 && (
            <div style={{ padding: "8px 16px", fontSize: 12, color: "var(--color-text-secondary)" }}>
              {pendingQ.isLoading ? "Loading…" : "Nothing scheduled — set a follow-up so a deal never goes quiet."}
            </div>
          )}
        </div>

        {/* Priority queue */}
        <div style={{ padding: "10px 0", borderBottom: "1px solid var(--color-border-subtle)" }}>
          <div style={{ padding: "0 16px" }}>
            <RailLabel icon={<Crosshair size={11} />} color="var(--color-text-secondary)">Priority queue · {queue.length}</RailLabel>
          </div>
          {queue.slice(1, 8).map((q) => (
            <button
              key={q.id}
              onClick={() => runAction(q)}
              style={{ display: "flex", alignItems: "flex-start", gap: 8, width: "100%", padding: "7px 16px", border: "none", background: "transparent", cursor: q.to || q.meetUri ? "pointer" : "default", textAlign: "left", transition: "background 140ms" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--color-bg-muted)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ width: 6, height: 6, borderRadius: 99, background: SEV_TONE[q.severity], flexShrink: 0, marginTop: 5 }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 12.5, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.title}</span>
                <span style={{ display: "block", fontSize: 11, color: "var(--color-text-secondary)" }}>{q.evidence}</span>
              </span>
              {(q.to || q.meetUri) && <ChevronRight size={13} style={{ color: "var(--color-text-secondary)", flexShrink: 0, marginTop: 3 }} />}
            </button>
          ))}
          {queue.length <= 1 && (
            <div style={{ padding: "8px 16px", fontSize: 12, color: "var(--color-text-secondary)" }}>Queue is clear.</div>
          )}
        </div>

        {/* Meetings */}
        {meetings.length > 0 && (
          <div style={{ padding: "10px 0", borderBottom: "1px solid var(--color-border-subtle)" }}>
            <div style={{ padding: "0 16px" }}>
              <RailLabel icon={<Calendar size={11} />} color="var(--crm-info)">Upcoming meetings</RailLabel>
            </div>
            {meetings.slice(0, 3).map((m) => (
              <div key={m._id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 16px" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.title}</div>
                  <div style={{ fontSize: 11, color: "var(--color-text-secondary)", fontFamily: MONO }}>
                    {meetingTime(m.scheduledStart)}{m.lead?.title ? ` · ${m.lead.title}` : ""}
                  </div>
                </div>
                {m.meetUri && (
                  <a href={m.meetUri} target="_blank" rel="noopener noreferrer"
                    style={{ height: 26, padding: "0 9px", borderRadius: 6, background: "var(--crm-success)", color: "#fff", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none", flexShrink: 0 }}>
                    <Video size={11} /> Join
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Live pulse */}
        <div style={{ padding: "10px 0 18px", flex: 1 }}>
          <div style={{ padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <RailLabel icon={<Radio size={11} />} color="var(--crm-success)">Live pulse</RailLabel>
            <span style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>latest {Math.min(6, recentActivities.length)}</span>
          </div>
          {recentActivities.length === 0 && (
            <div style={{ padding: "8px 16px", fontSize: 12, color: "var(--color-text-secondary)" }}>Quiet for now — activity appears as the team works.</div>
          )}
          <div style={{ padding: "0 16px" }}>
            {recentActivities.slice(0, 6).map((a) => <ActivityRow key={a._id} activity={a} compact />)}
          </div>
        </div>
      </aside>
    </div>
  );
}

function Chip({ tone, onClick, children }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      style={{
        padding: "5px 11px", borderRadius: 8, border: `1.5px solid ${tone}`,
        background: "var(--color-bg-surface)", color: tone, boxShadow: `0 0 14px -7px ${tone}`,
        fontFamily: MONO, fontSize: 11, fontWeight: 600, letterSpacing: "0.05em",
        cursor: onClick ? "pointer" : "default", whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}
